<template>
  <el-dialog
    class="dialog-wrapper measured-values"
    visible
    show-close
    :close-on-click-modal="false"
    :append-to-body="appendToBody"
    @close="abortInsert"
  >
    <ContentHeader
      slot="title"
      :title="$t('sampleDetails.measuredValues.title')"
    />
    <BulkCall
      ref="refBulk"
      :function="insertValue"
      :arguments="operationValuesArr"
      @completed="bulkCompleted"
    />
    <ValidationObserver
      v-if="!apiCallInProgress"
      ref="expectedValuesForm"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <FormOperationValue
          :hasValue="false"
          :operationValue="formDataBulk"
          :singleEdit="false"
          :sample="sample"
        />

        <div class="table-wrapper">
          <el-table
            class="width-100"
            :data="operationValues"
            :row-class-name="tableRowClassName"
            border
          >
            <el-table-column
              :label="$t('filters.operation')"
              prop="name"
            />
            <el-table-column :label="$t('sampleDetails.measuredValues.value')">
              <template slot-scope="scope">
                <FormItem marginBottomSize="none">
                  <div class="value-detail">
                    <el-input
                      v-model="scope.row.value"
                      :disabled="!scope.row.isAllowed"
                      @input="(value) => checkValidation(scope.row, value)"
                    />
                    <el-popover
                      v-if="!!scope.row.isNotCompliant || !scope.row.isAllowed"
                      placement="top-start"
                      trigger="hover"
                      :content="errorMessage(scope.row)"
                    >
                      <i
                        slot="reference"
                        class="fa fa-exclamation-triangle"
                      ></i>
                    </el-popover>
                    <i
                      v-if="!!scope.row.isLoading"
                      class="fa fa-circle-notch fa-spin"
                    ></i>
                  </div>
                </FormItem>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortInsert"
          >
            {{ $t('global.cancel') }}
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            :disabled="invalid || submitInvalidBulk"
            @click.prevent="handleSubmit(insertValues)"
          >
            {{ $t('global.confirm') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import moment from 'moment';
  import lodashDebounce from 'lodash.debounce';
  import { samplesApi, analysesApi, analysesDataApi } from '@/api';

  import FormOperationValue from '@/components/FormOperationValue.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import FormItem from '@/components/FormItem.vue';
  import BulkCall from '@/components/BulkCall.vue';

  const initialBulkData = {
    // value: '',
    LD: '',
    LQ: '',
    U: '',
    analysisStart: '',
    analysisEnd: '',
    measureId: null,
    measure: null,
    notes: '',
    recoveryFactor: null,
  };

  export default {
    name: 'InsertMeasuredValuesBulk',
    components: {
      FormOperationValue,
      ContentHeader,
      FormButtons,
      FormItem,
      BulkCall,
    },
    props: {
      sample: { type: Object, required: true },
      analysis: { type: Object, required: true },
      appendToBody: { type: Boolean, default: false },
    },
    data() {
      return {
        formDataBulk: { ...initialBulkData },
        operationValues: [],
        apiCallInProgress: false,
        genericBulkArguments: [],
        hasInitialSpecification: false,
      };
    },
    computed: {
      operationValuesArr() {
        return this.operationValues.map((o) => [o]);
      },
      submitInvalidBulk() {
        const allSetValues = this.operationValues.every(
          (operation) => operation.value !== ''
        );
        if (
          this.formDataBulk.analysisEnd !== '' &&
          this.formDataBulk.analysisEnd !== null
        ) {
          if (allSetValues) {
            return false;
          }
          return true;
        }
        if (
          allSetValues &&
          (this.formDataBulk.analysisEnd === '' ||
            this.formDataBulk.analysisEnd === null)
        ) {
          return true;
        }
        return false;
      },
    },
    beforeDestroy() {
      if (this.debouncedValidation) {
        this.debouncedValidation.cancel();
      }
    },
    watch: {
      analysis: {
        handler: 'initializeComponent',
        immediate: true,
      },
      'formDataBulk.specification': {
        handler(newSpec, oldSpec) {
          if (newSpec !== oldSpec) {
            this.operationValues.forEach((row) => {
              // Reset spinner immediately
              this.$set(row, 'isLoading', false);
              // Re-validate if a value has been set
              if (row.value !== '') {
                this.checkValidation(row, row.value);
              }
            });
          }
        },
      },
    },
    methods: {
      insertValue(operationValue) {
        const { measure, thermostat, temperature, ...formData } =
          this.formDataBulk;

        const { operationId, labSampleAnalysisId, value, valueId } =
          operationValue;

        const valuesData = {
          ...formData,
          value,
          recoveryFactor: this.parseDecimal(formData.recoveryFactor),
          analysisStart: moment
            .utc(formData.analysisStart)
            .startOf('day')
            .toDate(),
          analysisEnd: moment.utc(formData.analysisEnd).startOf('day').toDate(),
          labSampleAnalysisId,
          operationRegistryId: operationId,
        };
        const { specification, ...otherData } = valuesData;
        if (!this.hasInitialSpecification && specification) {
          otherData.specificationType = 'FreeText';
          otherData.specificationValue = specification;
        }
        delete otherData.symbol;
        delete otherData.symbolType;
        delete otherData.symbolLot;
        if (!operationValue.mainResult) {
          delete otherData.measureId; // unset the value if not main result, otherwise it will overwritten
        }
        if (valueId) {
          return samplesApi.updateSampleOperationValues(valueId, otherData);
        }
        return samplesApi.insertSampleOperationValues(otherData);
      },
      insertValues() {
        this.apiCallInProgress = true;
        this.$refs.refBulk.run();
      },
      populateInitialValues(values) {
        const {
          // value,
          LD,
          LQ,
          U,
          analysisStart,
          analysisEnd,
          measureId,
          measure,
          thermostatId,
          thermostat,
          temperatureId,
          temperature,
          notes,
          specification,
          recoveryFactor,
        } = values;

        this.formDataBulk = {
          // value: value || '',
          LD: LD || '',
          LQ: LQ || '',
          U: U || '',
          measureId,
          measure,
          thermostatId,
          thermostat,
          temperatureId,
          temperature,
          analysisStart: analysisStart
            ? moment.utc(analysisStart).startOf('day').toDate()
            : '',
          analysisEnd: analysisEnd
            ? moment.utc(analysisEnd).startOf('day').toDate()
            : '',
          notes: notes || '',
          specification: specification || '',
          recoveryFactor: recoveryFactor || null,
        };
      },
      parseDecimal(value) {
        if (value == null || value === '') return null;
        return parseFloat(String(value).replace(',', '.'));
      },
      abortInsert() {
        this.$emit('dialogClose', { success: this.apiCallInProgress });
      },
      bulkCompleted(errors) {
        if (!errors) {
          this.$emit('dialogClose', { success: true });
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('global.success'),
          });
        }
      },
      tableRowClassName({ row }) {
        if (row.mainResult) return 'highlighted-row';
        return '';
      },
      checkValidation(row, value) {
        this.$set(row, 'isLoading', true);
        this.$set(row, 'isNotCompliant', false);
        row.debouncedValidation(row, value);
      },
      async formValidation(row, value) {
        const payload = {
          id: row.valueId,
          labSampleAnalysisId: row.labSampleAnalysisId,
          operationRegistryId: row.operationRegistryId,
          value,
        };

        if (!this.hasInitialSpecification && this.formDataBulk.specification) {
          payload.specificationType = 'FreeText';
          payload.specificationValue = this.formDataBulk.specification;
        }

        const validation = await analysesApi.validateOperation(payload);
        this.$set(row, 'isLoading', false);
        this.$set(row, 'isNotCompliant', !validation.data.isValid);
      },
      errorMessage(row) {
        const validationMessage = row.isNotCompliant
          ? this.$t('sampleDetails.measuredValues.valueWarningMessage')
          : '';
        const allowedMessage = !row.isAllowed ? row.allowedMessage : '';
        return `${validationMessage} ${allowedMessage}`;
      },
      async initializeComponent() {
        const getAnalysisSpec = (op) => {
          return op && op.specification;
        };
        const getOpSpec = (opValue) => {
          return (
            opValue &&
            opValue.specificationType !== 'None' &&
            opValue.specificationValue
          );
        };
        const mainOp = this.analysis.children.find((c) => c.mainResult) || null;
        if (this.analysis.children[0].value) {
          const [child] = this.analysis.children;
          const displayValue = mainOp ? mainOp.value : child.value;
          if (mainOp && mainOp.mainOpMeasure) {
            displayValue.measure = mainOp.mainOpMeasure;
          }
          if (mainOp && mainOp.value && mainOp.value.measure) {
            displayValue.measure = mainOp.value.measure;
          }
          if (child && child.value && child.value.measure) {
            displayValue.measure = child.value.measure;
          }

          const analysisSpec = getAnalysisSpec(mainOp ?? child);
          const opSpec = getOpSpec(mainOp ? mainOp.value : child.value);

          this.populateInitialValues({
            ...displayValue,
            specification: analysisSpec || opSpec,
          });
        } else {
          this.formDataBulk = {
            ...this.formDataBulk,
            measure:
              (mainOp && mainOp.measure) || {
                id: mainOp && mainOp.measureId,
                description: mainOp && mainOp.measureDescription,
              } ||
              null,
            measureId: (mainOp && mainOp.measureId) || null,
            specification: (mainOp && mainOp.specification) || null,
          };
        }
        this.hasInitialSpecification = !!this.formDataBulk.specification;
        this.operationValues = this.analysis.children.map((operation) => {
          const debouncedValidation = lodashDebounce(this.formValidation, 800);
          return {
            operationId: operation.operationRegistryId,
            name: operation.operation,
            labSampleAnalysisId: operation.labSampleAnalysisId,
            operationRegistryId: operation.operationRegistryId,
            value: (operation.value && operation.value.value) || '',
            valueId: (operation.value && operation.value.id) || null,
            mainResult: operation.mainResult,
            isNotCompliant: false,
            isLoading: false,
            measureId: operation.measureId,
            debouncedValidation,
          };
        }); // check if a values can be entered
        const operationRegistryIds = this.operationValues.map(
          (op) => op.operationRegistryId
        );
        const response = await analysesDataApi.checkValueInsertion(
          this.analysis.labSampleAnalysisId,
          operationRegistryIds
        );
        // ------
        this.operationValues = this.operationValues.map((op) => ({
          ...op,
          isAllowed: response.data[op.operationRegistryId].allowed,
          allowedMessage: response.data[op.operationRegistryId].reason || '',
        }));
        // TODO: manage value validation at initialisation
        // this.formValidation(this.operationValues.value);
      },
    },
  };
</script>

<style lang="scss">
  .measured-values {
    .table-wrapper .el-table .el-table__row.highlighted-row {
      background-color: $--table-row-hover-background-color;
    }
    .value-detail {
      display: flex;
      align-items: center;

      i {
        margin-left: 10px;
      }
    }
  }
</style>
